/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import {
  CloudResourceReportResultFragment,
  CloudResourceReportsSortFields,
  SortOrder,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { Pagination } from "~/components/Pagination";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { useGetCloudResourceReports } from "~/views/resources_v2/hooks";
import { ReportsQuery } from "~/views/resources_v2/types";
import { columns } from "./ResourceReportsColumns";
import { ResourceReportsListHeader } from "./ResourceReportsListHeader";

export function ResourceReportsTable() {
  const { queryState } = useQueryState<ReportsQuery>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { loading, data, count } = useGetCloudResourceReports({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? CloudResourceReportsSortFields.DateGenerated,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const reports = data.filter(isNotNil) ?? [];

  return (
    <>
      <ResourceReportsListHeader />
      <LoaderWrap loading={loading} inContent>
        <If condition={isEmpty(reports)}>
          <Then>
            <NoData hasIcon message={"There are no Reports."} />
          </Then>
          <Else>
            <TableWrapper<CloudResourceReportResultFragment>
              loading={loading}
              data={reports}
              columns={columns()}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              stripped
            />
            <Pagination count={count} />
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
